import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import { colors } from "../../theme/palette";
import ChatWindow from "./ChatWindow";
import { Chat } from "../../types";
import ChatSideBar from "./ChatSideBar";
import { auth } from "../../firebase/firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { subscribeForChatUpdate } from "./firestore";
import { useProfileData } from "../../hooks/useProfileData";

const ChatContainer = () => {
  const [user] = useAuthState(auth);
  const [chats, setChats] = useState<Chat[]>([]);
  const [isChatsListLoading, setIsChatsListLoading] = useState(false);
  const [isUnreadActive, setIsUnreadActive] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState("");

  const { data, isLoading: isUserDataLoading, isError } = useProfileData();

  const { id: userId } = data;

  useEffect(() => {
    if (isUserDataLoading || !userId) return;

    // Subscribe to chat updates
    const fetchData = async () => {
      const unsubscribe = await subscribeForChatUpdate(userId, (chatList) => {
        setIsChatsListLoading(true);
        if (isUnreadActive) {
          const UNREAD_MSGS = chatList.filter((chat: Chat) => {
            if (chat.lastMessage.senderId === userId) return false;
            return chat.lastSeen?.[userId] < chat.lastMessage?.createdAt;
          });

          setChats(UNREAD_MSGS);
        } else {
          setChats(chatList);
        }

        if (searchValue) {
          setChats((prevChats) =>
            prevChats.filter((chat: Chat) => {
              if (chat.participants?.length > 1) {
                const receiverId = chat.participants.find(
                  (id) => id !== userId
                );
                if (receiverId && chat.participantsNames?.[receiverId]) {
                  return chat.participantsNames[receiverId]
                    .toLowerCase()
                    .includes(searchValue.toLowerCase());
                }
              }
              return false;
            })
          );
        }
        setIsChatsListLoading(false);
      });

      // Cleanup function to unsubscribe from onSnapshot when component unmounts or dependencies change
      return () => unsubscribe();
    };

    fetchData();
  }, [isUnreadActive, searchValue, userId, isUserDataLoading]);

  const isChatLoading = isUserDataLoading || isChatsListLoading || !userId;
  return (
    <Box
      sx={{
        height: "fit-content",
        width: "870px",
        minHeight: "958px",
        borderRadius: "8px",
        border: "1px solid #EAECF0",
        background: colors.White,
        display: "flex",
        flexDirection: "row",
        overflow: "hidden",
      }}
    >
      <ChatSideBar
        userId={userId}
        chats={chats}
        isUnreadActive={isUnreadActive}
        setIsUnreadActive={setIsUnreadActive}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        isLoading={isChatLoading}
      />
      <ChatWindow chats={chats} />
    </Box>
  );
};

export default ChatContainer;
